import { useEffect, useRef } from "react";
import { base64ToPdfBlob } from "./page-items";

type SurveyImageMap = Record<string, { contentType: string; data: string } | undefined>;

/**
 * Returns a stable map of document-id → object URL for every PDF entry in surveyImage.
 *
 * - One URL is created per document id; subsequent renders with the same surveyImage
 *   object reuse it without calling createObjectURL again.
 * - A URL is revoked as soon as its document disappears from surveyImage.
 * - All remaining URLs are revoked when the component unmounts.
 *
 * The cache is kept in a ref and updated synchronously during render so the URL is
 * available immediately (no one-render lag). The ref mutation is intentional: the
 * cache is not React state and must not trigger re-renders.
 */
export function usePdfObjectUrls(surveyImage: SurveyImageMap): Record<string, string> {
  const urlsRef = useRef<Record<string, string>>({});
  const prevSurveyImageRef = useRef<SurveyImageMap>({});

  if (prevSurveyImageRef.current !== surveyImage) {
    prevSurveyImageRef.current = surveyImage;

    const next: Record<string, string> = {};

    for (const [id, img] of Object.entries(surveyImage)) {
      if (img?.contentType === "application/pdf") {
        // Reuse the existing URL when the document has not changed.
        next[id] = urlsRef.current[id] ?? URL.createObjectURL(base64ToPdfBlob(img.data));
      }
    }

    // Revoke URLs for documents that have been removed from the map.
    for (const [id, url] of Object.entries(urlsRef.current)) {
      if (!next[id]) URL.revokeObjectURL(url);
    }

    urlsRef.current = next;
  }

  // Revoke all remaining URLs when the component unmounts.
  useEffect(
    () => () => {
      for (const url of Object.values(urlsRef.current)) {
        URL.revokeObjectURL(url);
      }
      urlsRef.current = {};
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return urlsRef.current;
}
